import { ExternalLink } from 'lucide-react';
import { trackingLink } from '../utils/mailTracking';

/**
 * A mail job's tracking number, linked to the carrier that has it.
 *
 * The carrier and the URL come from mailTracking.js, which already
 * decides both and is what the deal's own mail panel uses. This exists
 * only because the wall screen and the My Mail menu were printing the
 * number as text, so checking on a piece meant copying it and finding
 * the carrier's site by hand.
 *
 * When the carrier cannot be established, trackingLink returns null and
 * this renders plain text. That is deliberate and is the helper's rule,
 * not a new one: a link that opens the wrong carrier's "not found" page
 * is worse than the bare number, which can still be pasted somewhere
 * that works. The number always shows either way — it is the only
 * handle anybody has on a piece once it has gone.
 *
 * `returnLeg` asks about the envelope coming back rather than the one
 * going out. A deed packet has both, and they are different numbers.
 */
export default function TrackingNumber({ job, returnLeg = false, dark = false }) {
  const link = trackingLink(job, { returnLeg });
  const raw = returnLeg
    ? (job?.returnTrackingNumber || job?.return_tracking_number || '')
    : (job?.trackingNumber || job?.tracking_number || '');
  if (!link) {
    if (!raw) return null;
    return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{raw}</span>;
  }
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      data-mail-tracking-link
      title={`Track this piece on ${link.label}`}
      // The row this sits in is clickable on both screens.
      onClick={(event) => event.stopPropagation()}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        color: dark ? '#93c5fd' : '#0b5cab',
        fontVariantNumeric: 'tabular-nums',
        textDecoration: 'underline',
        textUnderlineOffset: 2,
      }}
    >
      {link.number}
      <ExternalLink size={11} style={{ flexShrink: 0 }} />
    </a>
  );
}
